import { buildingGroundZ } from "building-generation/elements/level/buildingGroundZ";
import {
  sectionViewFrontPlane,
  sectionViewSecondaryClipPlanes,
} from "building-generation/generationTools/sectionViewClippingPlane";
import type { Building } from "building-types/building/buildingTypes";
import { levelExtrusionsToExtrusions } from "building-types/levels/levelPerimeters";
import { sheetAddGeometries, sheetGeometryListNew } from "building-types/views/sheetGeometryTypes";
import type { SectionViewGeometry } from "building-types/views/View3D";
import { PDF_DPI } from "common/constants/pdfSettings";
import { assertIsNotUndefined, ensureDefined } from "common/utils/assertOfType";
import type { DrawingCamera } from "doc/plans/drawingCameraTypes";
import type { SheetGeometrySelectorEffectMap } from "doc/plans/SheetGeometrySelectorEffectMap";
import { aabb2ByMinXYWidthHeight } from "geom/aabb2";
import type { AABB2 } from "geom/aabb2Types";
import { aabb3New, type AABB3 } from "geom/aabb3Types";
import type { Face } from "geom/brep";
import { clipFacesWithPlanes } from "geom/bspClipping";
import type { PlaneBSP } from "geom/bspTypes";
import { DIST_EPSILON } from "geom/core";
import { extrusionToBRep } from "geom/extrude";
import type { Mat4 } from "geom/mat4";
import { mat4GetViewDirection, mat4Ident, mat4LookAt, mat4Mult, ndcToViewportMatrix } from "geom/mat4";
import { planeBSPFromPlane } from "geom/planeBSP";
import { drawingCameraToMatrices } from "lib/house/plans/drawingCameraToMatrices";
import { collectReportingUnitBuildingFaces } from "lib/house/plans/perspective";
import { perspectiveDrawingFinalCamera } from "lib/house/plans/perspectiveDrawingFinalCamera";
import type { MakeLinework3DGeometryListOptions } from "lib/house/plans/renderPerspective";
import { makeLinework3DGeometryList } from "lib/house/plans/renderPerspective";
import type { LineworkDrawingBase, OrthographicDrawing, PerspectiveDrawing } from "lib/house/plans/sheetNode";
import { renderBuildingOutlineFaces } from "lib/house/render/buildingOutlineRenderer";
import { renderViewSpaceBBFromVertices } from "lib/house/render/renderNDCBBFromBuilding";
import type { Linework3DGeometryAndBB } from "../plans/perspectiveTypes";
import type { PlanSetBSPProvider } from "./planSetBSPProvider";
import { renderSectionFrontPlaneIntersectionsGeometry } from "./renderSectionFrontPlaneIntersections";

async function commonLineworkDrawingParams(
  drawing: LineworkDrawingBase,
  building: Building,
  bspProvider: PlanSetBSPProvider,
) {
  const {
    sceneOptions,
    occlusionSceneOptions,
    useRoofVolumeBSP = false,
    renderingConfig,
    backgroundVisibility,
  } = drawing;

  const groundZ = buildingGroundZ(building);

  return {
    groundZ,
    sceneBSP: ensureDefined(await bspProvider.getSceneBSP(sceneOptions)),
    occlusionBSP: occlusionSceneOptions ? (await bspProvider.getSceneBSP(occlusionSceneOptions))?.root ?? null : null,
    roofVolumesBSP: useRoofVolumeBSP ? bspProvider.getRoofVolumeBSP() ?? null : null,
    renderingConfig,
    backgroundVisibility,
    visibilitySelectors: sceneOptions.visibilitySelectors,
  };
}

export async function renderBuildingPerspectiveDrawing(
  perspectiveDrawing: PerspectiveDrawing,
  building: Building,
  bspProvider: PlanSetBSPProvider,
): Promise<Linework3DGeometryAndBB> {
  const { width, height, camera } = perspectiveDrawing;

  assertIsNotUndefined(camera);
  const cameraToUse: DrawingCamera = await perspectiveDrawingFinalCamera(
    building,
    camera,
    perspectiveDrawing.autoCrop,
    height,
    perspectiveDrawing.autoCropPadding,
  );

  const { viewMatrix, projectionMatrix, clippingPlanes } = drawingCameraToMatrices(cameraToUse);

  // Scale the projection matrix into PDF space to play nicely with our epsilons and tolerances.
  const viewportMatrix = ndcToViewportMatrix(width * PDF_DPI, height * PDF_DPI, 10000);
  const finalProjectionMatrix = mat4Mult(viewportMatrix, projectionMatrix);

  const options: MakeLinework3DGeometryListOptions = {
    ...(await commonLineworkDrawingParams(perspectiveDrawing, building, bspProvider)),
    viewMatrix,
    projectionMatrix: finalProjectionMatrix,
    clippingPlanes,
    isPerspectiveMatrix: true,
    isElevation: false,
  };

  const planGeometryList = makeLinework3DGeometryList(options);
  const bb = aabb2ByMinXYWidthHeight(0, 0, width * PDF_DPI, height * PDF_DPI);

  return { planGeometryList, bb };
}

/**
 * Bounding box of an orthographic drawing using world space units.
 * View matrix should not have a scale component.
 * Does not clip to the camera near or far planes.
 * Also returns depth coordinates which can help with placing a camera for near/far clipping.
 */
export async function orthographicDrawingBB(
  building: Building,
  viewMatrix: Mat4,
  clipBBToGradeLine: boolean,
  visibilitySelectors?: SheetGeometrySelectorEffectMap[],
  sectionView?: SectionViewGeometry,
  overrideFaces?: Face[],
): Promise<AABB3> {
  if (!building) {
    return aabb3New(0, 0, 0, 0, 0, 0);
  }
  let faces = overrideFaces ?? (await renderBuildingOutlineFaces(building, visibilitySelectors));
  if (faces.length === 0) {
    return aabb3New(0, 0, 0, 0, 0, 0);
  }
  if (sectionView) {
    const frontPlane = sectionViewFrontPlane(sectionView, building.mirror, building.mirrorTranslationX);
    if (frontPlane) {
      const clippingPlanes = [
        frontPlane,
        ...sectionViewSecondaryClipPlanes(sectionView, building.mirror, building.mirrorTranslationX),
      ];

      // Ideally we'd want the volumetric intersection of the building and the section box here.

      // Clipping building outline faces are usually good approximation.
      // However, we can't do cap faces since the geometry is not necessarily fully closed.
      faces = clipFacesWithPlanes({ faces, planes: clippingPlanes, addCapFaces: false });

      // Clipped output faces will not always be enough since they don't cover the interior
      // volume of rooms in case the section box doesn't intersect the walls.
      // Clip levelExtrusions with cap faces to get a better approximation of the clipped interior volume.
      const extrusionFaces = levelExtrusionsToExtrusions(Object.values(building.levelExtrusions)).flatMap(
        (extrusion) => extrusionToBRep(extrusion).faces,
      );
      const clippedLevelExtrusionFaces = clipFacesWithPlanes({
        faces: extrusionFaces,
        planes: clippingPlanes,
        addCapFaces: true, // This should get us a volumetric intersection. We know that we're working with closed geometry here.
      });
      faces.push(...clippedLevelExtrusionFaces);
    }
  }

  const viewSpaceBB = renderViewSpaceBBFromVertices({
    faces,
    viewMatrix,
  });
  const [, , viewDirectionZ] = mat4GetViewDirection(viewMatrix);
  if (!sectionView && Math.abs(viewDirectionZ) < DIST_EPSILON && clipBBToGradeLine) {
    const groundZ = buildingGroundZ(building);
    viewSpaceBB.minY = Math.max(viewSpaceBB.minY, groundZ);
  }
  return viewSpaceBB;
}

export async function renderBuildingOrthographicDrawing(
  dwg: OrthographicDrawing,
  building: Building,
  bspProvider: PlanSetBSPProvider,
): Promise<Linework3DGeometryAndBB> {
  const { eyePos, sectionView } = dwg;

  assertIsNotUndefined(eyePos);
  const viewMatrix = mat4LookAt(eyePos, [0, 0, 0], dwg.upVector ?? [0, 0, 1]);
  const projectionMatrix = mat4Ident();

  const mirror = building.mirror;
  const mirrorTranslationX = building.mirrorTranslationX;

  const sectionClippingPlane = sectionView ? sectionViewFrontPlane(sectionView, mirror, mirrorTranslationX) : undefined;
  const clippingPlanes: PlaneBSP[] = [];
  if (sectionView) {
    clippingPlanes.push(
      ...sectionViewSecondaryClipPlanes(sectionView, mirror, mirrorTranslationX).map((plane) =>
        planeBSPFromPlane(plane),
      ),
    );
  }
  const usesBelowGroundLines =
    dwg.renderingConfig &&
    dwg.renderingConfig.layers.find(
      (layer) => layer.source === "belowGroundLines" || layer.source === "belowGroundHatchLines",
    ) !== undefined;
  const unitFaces = dwg.sceneOptions.selectedReportingUnitLabelId
    ? collectReportingUnitBuildingFaces(
        building,
        dwg.sceneOptions.faceCategoryFilter,
        dwg.sceneOptions.selectedReportingUnitLabelId,
      )
    : undefined;
  const bb: AABB2 = await orthographicDrawingBB(
    building,
    viewMatrix,
    !usesBelowGroundLines,
    dwg.sceneOptions.visibilitySelectors,
    sectionView,
    unitFaces,
  );

  const options: MakeLinework3DGeometryListOptions = {
    ...(await commonLineworkDrawingParams(dwg, building, bspProvider)),
    viewMatrix,
    projectionMatrix,
    isPerspectiveMatrix: false,
    isElevation: dwg.isElevation,
    sectionClippingPlane,
    clippingPlanes,
  };

  const planGeometryList = makeLinework3DGeometryList(options);

  if (sectionView) {
    const faceCategoryFilter = dwg.sceneOptions.faceCategoryFilter;
    const planGeometryListWithSectionIntersections = sheetGeometryListNew();
    const intersectionsGeometry = renderSectionFrontPlaneIntersectionsGeometry(building, sectionView);
    for (const key in intersectionsGeometry) {
      if (!faceCategoryFilter || faceCategoryFilter.has(parseInt(key))) {
        sheetAddGeometries(planGeometryListWithSectionIntersections, intersectionsGeometry[key].geometry);
      }
    }
    sheetAddGeometries(planGeometryListWithSectionIntersections, planGeometryList.geometry);
    return { planGeometryList: planGeometryListWithSectionIntersections, bb };
  }

  return { planGeometryList, bb };
}
